import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import Axios from 'axios';
import { getAuthHeader } from '../../utils/authToken';
const Context = createContext(null);
export const accessURL = `${process.env.REACT_APP_URL}/api/v1/payment-links`;
export const accessRequest = async (method, path, data) => (await Axios({ method, url: `${accessURL}${path}`, data,
    headers: { Authorization: getAuthHeader() }, timeout: 30000 })).data;
export const usePaymentLinkAccess = () => useContext(Context);
export function PaymentLinkAccessProvider({ children }) {
    const { pathname } = useLocation();
    const [state, setState] = useState({ loading: true, path: null, error: '', can_access_payment_links: false, can_manage_access: false });
    const sequence = useRef(0);
    const refresh = useCallback(async () => {
        const request = ++sequence.current;
        setState(previous => ({ ...previous, loading: true }));
        try {
            const data = await accessRequest('get', '/access');
            if (request === sequence.current) setState({ ...data, loading: false, path: pathname, error: '' });
        } catch {
            if (request === sequence.current) setState({ loading: false, path: pathname, error: 'Could not verify Payment Links access.', can_access_payment_links: false, can_manage_access: false });
        }
    }, [pathname]);
    // This ref counts requests; cleanup intentionally invalidates the latest responses, not a DOM node.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { refresh(); return () => { sequence.current++; }; }, [refresh]);
    useEffect(() => { window.addEventListener('focus', refresh); return () => window.removeEventListener('focus', refresh); }, [refresh]);
    const deny = useCallback(() => { sequence.current++; setState(previous => ({ ...previous, loading: false, path: pathname, can_access_payment_links: false })); }, [pathname]);
    return <Context.Provider value={{ ...state, loading: state.loading || state.path !== pathname, refresh, deny }}>{children}</Context.Provider>;
}
export function PaymentLinkAccessGuard({ children }) {
    const access = usePaymentLinkAccess();
    if (!access || access.loading) return <Box p={3}><CircularProgress size={24} aria-label="Checking Payment Links access" /></Box>;
    if (access.error) return <Box p={3}><Alert severity="error" action={<Button onClick={access.refresh}>Retry</Button>}>{access.error}</Alert></Box>;
    if (!access.can_access_payment_links) return <Box p={3}><Alert severity="warning">You do not have access to Payment Links.</Alert><Button component={Link} to="/admin/home" sx={{ mt: 2 }}>Back to dashboard</Button></Box>;
    return children;
}
